import React from 'react';
import { useNavigate } from 'react-router-dom';
import useModuleProgress from '../hooks/useModuleProgress';
import '../styles/pages/_module-pages.scss';

interface ModuleSection {
  title: string;
  content: string[];
  illustrationText?: string;
  additionalSection?: {
    title: string;
    content: string[];
  };
}

// Define the content for the multi-step text module
const moduleContent: ModuleSection[] = [
  {
    title: 'What is Spiritual Wellness? (The Core Idea)',
    content: [
      '<strong>Many people confuse spirituality with religion, but this module separates them clearly:</strong>',
      "• <strong>Spirituality is a personal search</strong> for what makes your life feel meaningful, connected, and peaceful. It's how you relate to yourself, others, nature, and the present moment. You can be deeply spiritual whether or not you belong to a religious organization.",
      '• <strong>Religion</strong> often involves shared doctrines, organized practices, and communal worship.',
    ],
    additionalSection: {
      title: 'Your Personal Benefit',
      content: [
        "By defining spirituality this way, the module makes it an actionable skill you can develop daily. It's about how you live your values."
      ]
    }
  },
  {
    title: 'What You Will Gain (The Learning Objectives)',
      content: [
      'By the end of this module, you won\'t just learn definitions–you\'ll gain new perspectives and tools:',
      '<strong>1. See Spirituality as a Key to a Healthier Life:</strong> This establishes spirituality as one pillar (alongside physical, emotional, and mental) essential for overall well-being.',
      '<strong>2. Appreciate Your Own Path:</strong> You\'ll learn to respect your personal spiritual journey while recognizing that spirituality and religion are distinct concepts.',
      '<strong>3. Find Your Five Spiritual Strengths:</strong> You\'ll identify the five practical ways spirituality shows up in your daily actions, giving you a clear focus for growth.',
    ]
  },
  {
    title: 'The Five Pillars of a Meaningful Life (Key Concepts)',
    content: [
      'This section provides the working framework for spiritual wellness, defining it as the alignment of values, actions, and purpose. The five domains are the behavioral expressions and measurable indicators of this alignment:',
      `<div class="module-table">
        <div class="table-row header-row">
          <div class="table-header">Spiritual Pillar</div>
          <div class="table-header">What It Means</div>
          <div class="table-header">Why It Matters to You</div>
        </div>
        <div class="table-row">
          <div class="table-cell pillar-cell">Connectedness</div>
          <div class="table-cell means-cell">Feeling bonded to the people around you, your community, or a natural home.</div>
          <div class="table-cell matters-cell">Combats loneliness; builds stronger relationships and a sense of belonging.</div>
        </div>
        <div class="table-row">
          <div class="table-cell pillar-cell">Mindfulness</div>
          <div class="table-cell means-cell">Being fully present in the current moment without judgment (not dwelling on the past or worrying about the future).</div>
          <div class="table-cell matters-cell">Reduces anxiety; helps you appreciate life as it happens; improves focus.</div>
        </div>
        <div class="table-row">
          <div class="table-cell pillar-cell">Compassion</div>
          <div class="table-cell means-cell">Showing genuine kindness and empathy toward both yourself and others.</div>
          <div class="table-cell matters-cell">Heals self-criticism; improves conflict resolution; fosters forgiveness.</div>
        </div>
        <div class="table-row">
          <div class="table-cell pillar-cell">Gratitude</div>
          <div class="table-cell means-cell">Actively noticing and appreciating the good things, large and small, in your life.</div>
          <div class="table-cell matters-cell">Shifts your perspective from what you lack to what you have, increasing happiness.</div>
        </div>
        <div class="table-row">
          <div class="table-cell pillar-cell">Inner Peace</div>
          <div class="table-cell means-cell">Achieving a state of emotional and mental calmness and stability.</div>
          <div class="table-cell matters-cell">Provides resilience during stress; allows you to respond thoughtfully instead of reacting emotionally.</div>
        </div>
      </div>`,
      'Working on these five areas provides you with resilience during tough times, promotes emotional balance, and clarifies your sense of identity and purpose.',
    ],
  },
  {
    title: 'Your Next Step: Personal Reflection',
    content: [
      'The module concludes with the most important part–making it personal.',
      '• <strong>Question:</strong> "When do I feel most connected to something greater than myself?"',
      '• <strong>Exercise:</strong> By journaling for 5 minutes and sharing, you turn an abstract idea into a concrete, personal discovery. This helps you realize that you are already spiritual; you just need to consciously recognize and nurture those moments.',
    
    ],
    illustrationText: 'Community & Joy',
  },
  {
    title: 'Ready to Test Your Knowledge?',
    content: [
      'You\'ve completed the module content! Now, let\'s reinforce your learning with a quick quiz to check your understanding.'
    ],
    additionalSection: {
      title: 'Next Steps',
      content: [
        'Click "Take the Quiz" below to test your knowledge about spiritual wellness fundamentals.',
        'The quiz will help you review key concepts about spirituality, the five pillars of spiritual wellness, and finding meaning in your life.'
      ]
    }
  },
];

// Main component, matching the design of the standard assessment form
const SpiritualM1: React.FC = () => {
  const navigate = useNavigate();
  const { currentStep: rawCurrentStep, setCurrentStep, isLoading, saveProgress, isCompleted } = useModuleProgress('spiritualM1');
  const totalSteps = moduleContent.length;

  // Ensure currentStep is within valid bounds
  const currentStep = Math.min(rawCurrentStep, totalSteps - 1);

  const currentModule = moduleContent[currentStep];
  const progress = ((currentStep + 1) / totalSteps) * 100;

  const handleNext = async () => {
    if (currentStep < totalSteps - 1) {
      const nextStep = currentStep + 1;
      setCurrentStep(nextStep);
      await saveProgress(nextStep, false);
    } else {
      // Mark the module as completed and navigate to quiz
      await saveProgress(currentStep, true);
      navigate('/panel/learn/spiritual-module-1/quiz');
    }
  };

  const handleBack = async () => {
    if (currentStep === 0) {
      navigate('/panel/learn/spiritual-resources');
      return;
    }
    const prevStep = currentStep - 1;
    setCurrentStep(prevStep);
    await saveProgress(prevStep, isCompleted);
  };

  // Check if we're on the last step (Ready to Test Your Knowledge page)
  const showCompletionMessage = currentStep === totalSteps - 1;

  if (isLoading) {
    return (
      <div className="module-form" style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh'
      }}>
        <p>Loading your progress...</p>
      </div>
    );
  }

  return (
    // 1. Updated wrapper class: module-form-page -> module-form
    <div className="module-form">
      <div className="module-container">
        
        {/* Left Panel - Illustration */}
        {/* 2. Updated left panel class: module-left-panel -> module-left */}
        <div 
          className="module-left spiritual-panel"
        >
          {/* 3. Retaining module-header for left panel content */}
          <div className="module-header">
            <h1>Module 1</h1>
            <p className="module-subtitle">Understanding Spiritual Wellness</p>
            <hr className="module-divider" />
            <p className="module-tool-info">
              This module is your guide to understanding and strengthening a vital, often overlooked part of your life: <strong>Spiritual Wellness</strong>. 
              It moves beyond traditional ideas of faith to focus on finding meaning, connection, and peace right where you are.
            </p>
          </div>
        </div>

        {/* Right Panel - Content */}
        {/* 4. Updated right panel class: module-right-panel -> module-right */}
        <div className="module-right">
          
          {/* Content Wrapper for flex space-between layout */}
          <div className="module-content"> 
          
            {/* Progress Bar */}
            <div className="module-progress">
              <div className="progress-bar">
                <div className="progress-fill" style={{ width: `${progress}%` }}></div>
              </div>
              <span className="progress-text">
                Page {currentStep + 1} of {totalSteps}
              </span>
            </div>

            {/* Content Section */}
            <div className="module-section">
              <h2 className="module-title"> 
                {` ${currentModule.title}`}
              </h2>
              <div className="content-section">
                {currentModule.content.map((paragraph, index) => (
                  <p key={index} className="content-paragraph" dangerouslySetInnerHTML={{ __html: paragraph }} />
                ))}
              </div>
            </div>

            {/* Additional Title Section */}
            {currentModule.additionalSection && (
              <div className="module-section">
                <h2 className="module-title">{currentModule.additionalSection.title}</h2>
                <div className="content-section">
                  {currentModule.additionalSection.content.map((paragraph: string, index: number) => (
                    <p key={`additional-${index}`} className="content-paragraph">{paragraph}</p>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Navigation Buttons */}
          <div className="navigation-buttons">
            <button
              className="btn-secondary"
              onClick={handleBack}
              // Logic moved to handleBack, so button is never disabled until the end state is reached.
              disabled={false} 
            >
              {currentStep === 0 ? 'Exit Module' : 'Back'}
            </button>
            
            <button
              className="btn-primary"
              onClick={handleNext}
            >
              {showCompletionMessage ? 'Take the Quiz' : 'Next'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SpiritualM1;

